import { useRef, useState, useEffect, useCallback, FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { localStorage } from 'dibs-browser-storage';
import {
    trackEvent,
    eventNameConstants,
    stepInteractionConstants,
    interactionTypeConstants,
} from 'dibs-tracking';
const { EVENT_CONTACT_DEALER } = eventNameConstants;
const { INTERACTION_TYPE_CONTACT_DEALER } = interactionTypeConstants;
const { STEP_INTERACTION_PDP_CHAT_BOX_DISPLAY } = stepInteractionConstants;

// components
import { BuyerChatRespLazy } from 'dibs-buyer-chat/exports/BuyerChatRespLazy';
import {
    VisibilityTracker,
    VisibilityChangeCallback,
} from 'dibs-visibility-tracker/exports/VisibilityTracker';

// types
import { PdpRespSharedSellerChatLoader_viewer$key as Viewer } from './__generated__/PdpRespSharedSellerChatLoader_viewer.graphql';

const SESSION_CHAT_STATE_KEY = 'PDP_CHAT_STATE' as const;

const trackChatOpen = (isMinimized: boolean): void => {
    const label = `pdp chat box ${isMinimized ? 'minimized' : 'open'}`;
    trackEvent(
        {
            category: 'contact dealer',
            action: 'pdp chat box displayed',
            label,
            eventName: EVENT_CONTACT_DEALER,
            interaction_type: INTERACTION_TYPE_CONTACT_DEALER,
            step_interaction_name: STEP_INTERACTION_PDP_CHAT_BOX_DISPLAY,
            trigger: label,
        },
        null
    );
};

const isMinimizedLocalStorage = (): boolean => {
    const { isMinimized = false } =
        (localStorage.getItem(SESSION_CHAT_STATE_KEY) as { isMinimized?: boolean }) || {};
    return isMinimized;
};

const viewerFragment = graphql`
    fragment PdpRespSharedSellerChatLoader_viewer on Viewer {
        ...BuyerChatRespLazy_viewer
    }
`;

export const PdpRespSharedSellerChatLoader: FC<{ viewer: Viewer; currency: string }> = ({
    currency,
    viewer: viewerRef,
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const chatVisibilityRef = useRef(null);
    const [isMinimized, setIsMinimized] = useState(false);
    const [isChatVisible, setIsChatVisible] = useState(false);

    useEffect(() => {
        if (isChatVisible) {
            const isInitiallyMinimized = isMinimizedLocalStorage();
            trackChatOpen(isInitiallyMinimized);
        }
    }, [isChatVisible]);

    useEffect(() => {
        const isInitiallyMinimized = isMinimizedLocalStorage();
        setIsMinimized(isInitiallyMinimized);
    }, []);

    const handleVisibilityChange = useCallback(
        ({ isVisible, boundingClientRect, disconnect }: VisibilityChangeCallback) => {
            if (isVisible || boundingClientRect?.top < 0) {
                setIsChatVisible(true);
                disconnect();
            }
        },
        []
    );

    return (
        <>
            <div ref={chatVisibilityRef} />
            <VisibilityTracker
                elementRef={chatVisibilityRef}
                onVisibilityChange={handleVisibilityChange}
            />
            {isChatVisible && (
                <BuyerChatRespLazy
                    viewer={viewer}
                    currency={currency}
                    isInitiallyMinimized={isMinimized}
                />
            )}
        </>
    );
};
